import { EmailHint } from "@/components/EmailHint";
import { useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Lock } from "lucide-react";
import { SensitiveCell } from "@/components/SensitiveCell";
import { RadioOptions } from "@/components/RadioOptions";
import { AddressInput } from "@/components/AddressInput";
import { InstitutionInput } from "@/components/InstitutionInput";
import { ContactInput } from "@/components/ContactInput";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { formatAddressSummary } from "@shared/address";
import { formatPlannerCurrency } from "@/lib/estate-format";
import type { GenericField } from "@shared/schema";

function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  // Parse ISO date-only strings as local time, not UTC, to avoid timezone rollback.
  // new Date('YYYY-MM-DD') is treated as UTC midnight by spec; constructing via
  // new Date(y, m, d) uses local time and prevents off-by-one-day display errors.
  const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (parts) {
    const d = new Date(parseInt(parts[1]), parseInt(parts[2]) - 1, parseInt(parts[3]));
    return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
  }
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
}

export interface EditableCellProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  type?: "text" | "password" | "tel" | "currency" | "date" | "email" | "number" | "yes_no";
  options?: string[];
  placeholder?: string;
  readOnly?: boolean;
  testId?: string;
  multiline?: boolean;
  showBorder?: boolean;
  className?: string;
  ariaLabel?: string;
  /** Cat A intercept — when true and field/clientId are provided, renders SensitiveCell. */
  isCategoryA?: boolean;
  /** Compact mode — table cells render an address as a one-line summary that opens
   *  AddressInput in a popover, instead of the tall inline 6-box editor. */
  compact?: boolean;
  field?: Pick<GenericField, "id" | "name" | "fieldKey" | "fieldType">;
  clientId?: string;
  /** Row id for the Category A store key (table/expandable rows); null for card (s88). */
  rowId?: string | null;
  secureFileSetupAt?: Date | null;
}

export function EditableCell({
  value,
  onChange,
  onBlur,
  type = "text",
  options,
  placeholder,
  readOnly,
  testId,
  multiline,
  showBorder,
  className,
  ariaLabel,
  isCategoryA,
  compact,
  field,
  clientId,
  rowId,
  secureFileSetupAt,
}: EditableCellProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isCurrencyFocused, setIsCurrencyFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Cat A intercept — editable Cat A cells route to SensitiveCell for SIF storage.
  // ReadOnly Cat A cells fall through to the existing password-type rendering below.
  if (isCategoryA && field && clientId && !readOnly) {
    return (
      <SensitiveCell
        field={field}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        clientId={clientId}
        rowId={rowId}
        secureFileSetupAt={secureFileSetupAt ?? null}
      />
    );
  }

  // Structured-address intercept — fieldType "address" renders the multi-field
  // editor (table + expandable drawer both reach EditableCell). Self-detected
  // from `field`, so deriveCellInputType and the table call-site type-ternaries
  // need no "address" case. Addresses are never Category A, so this never
  // competes with the SensitiveCell intercept above; AddressInput handles its
  // own readOnly summary.
  if (field?.fieldType === "address") {
    const addressInput = (
      <AddressInput
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        readOnly={readOnly}
        fieldId={field.id}
      />
    );
    // Compact (table collapsed-row) mode: render a one-line summary as the cell's
    // click-target; the full 6-box editor opens in a popover anchored to the cell.
    // Non-compact (drawer / card / table expanded-row sub-grid) keeps the inline
    // editor. ReadOnly always falls through to AddressInput's own summary span.
    if (compact && !readOnly) {
      const summary = formatAddressSummary(value);
      return (
        <Popover
          onOpenChange={(open) => {
            // Commit on every close (Escape, click-outside, programmatic). Radix
            // unmounts PopoverContent before a focused subfield's blur reaches the
            // cell's save path, so the Escape close would otherwise drop the edit.
            // The parent blur handler is idempotent (no-ops when nothing changed).
            if (!open) {
              onBlur?.();
            }
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`block h-8 w-full px-2 text-sm text-left leading-8 border border-border shadow-none bg-muted hover:bg-muted-foreground/20 rounded truncate cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:bg-background ${summary ? "" : "text-muted-foreground"}`}
              data-testid={testId}
              aria-label={ariaLabel}
            >
              {summary || "—"}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80">
            {addressInput}
          </PopoverContent>
        </Popover>
      );
    }
    return addressInput;
  }

  // Institution type-ahead intercept (INSTITUTION-TYPEAHEAD-1, D-202.3) — sits beside the
  // address branch and works the same way: self-detected from `field`, so no call-site
  // type-ternary needs an "institution" case. Institutions are never Category A, so this
  // never competes with the SensitiveCell intercept above.
  //
  // Unlike address, the control is a SINGLE input, so there is no tall inline editor to
  // avoid and no popover: compact table cells render the same control, just narrower. The
  // compact READ-ONLY path still needs the summary, which InstitutionInput renders itself.
  if (field?.fieldType === "institution") {
    return (
      <InstitutionInput
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        readOnly={readOnly}
        fieldId={field.id}
        placeholder={placeholder}
      />
    );
  }

  // Contact type-ahead intercept (PEOPLE-RECORD-1, D-202.1) — the institution branch's twin,
  // self-detected from `field` for the same reason, and reached by BOTH the table path and the
  // drawer: ExpandableSectionLayout's renderCell passes `field` straight through to this
  // component, which is why it needed no edit for the institution type and needs none for this
  // one. Contacts are never Category A, so this never competes with the SensitiveCell intercept.
  //
  // 🔴 `clientId` is REQUIRED for the suggestion list and the mint, and this component already
  // receives it for the Cat A path. Without it the control still accepts and saves free text —
  // it just cannot resolve or create — which is the correct degradation, not a broken cell.
  if (field?.fieldType === "contact") {
    return (
      <ContactInput
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        readOnly={readOnly}
        fieldId={field.id}
        clientId={clientId}
        placeholder={placeholder}
      />
    );
  }

  const baseClassName = showBorder
    ? "h-8 text-sm px-2 focus-visible:ring-1 focus-visible:ring-offset-0"
    : "h-8 text-sm border border-border shadow-none bg-muted hover:bg-muted-foreground/20 px-2 rounded focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:bg-background placeholder:text-muted-foreground/70";

  const multilineClassName =
    "min-h-[32px] text-sm border border-border shadow-none bg-muted hover:bg-muted-foreground/20 px-2 py-1 rounded focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:bg-background placeholder:text-muted-foreground/70 resize-none";

  if (readOnly) {
    if (type === "password") {
      if (!value) {
        return <span className="text-sm text-muted-foreground italic">—</span>;
      }
      if (value === "[REDACTED]") {
        return (
          <span className="font-mono text-sm text-muted-foreground flex items-center gap-1">
            <Lock className="h-3 w-3" />
            [REDACTED]
          </span>
        );
      }
      return (
        <div className="flex items-center gap-1">
          <span className="font-mono text-sm">
            {showPassword ? value : "••••••••"}
          </span>
          <Button aria-label={showPassword ? "Hide password" : "Show password"}
            type="button"
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={() => setShowPassword(!showPassword)}
            data-testid={testId ? `${testId}-toggle` : undefined}
          >
            {showPassword ? (
              <EyeOff className="h-3 w-3" />
            ) : (
              <Eye className="h-3 w-3" />
            )}
          </Button>
        </div>
      );
    }
    // Read-only currency: show formatted value
    if (type === "currency") {
      const display = value ? formatPlannerCurrency(value) : "";
      return (
        <span className="text-sm">
          {display || <span className="text-muted-foreground italic">—</span>}
        </span>
      );
    }
    // Read-only yes_no: render the stored "true"/"false" as Yes/No.
    if (type === "yes_no") {
      return (
        <span className="text-sm">
          {value === "true" ? "Yes" : value === "false" ? "No" : <span className="text-muted-foreground italic">—</span>}
        </span>
      );
    }
    return (
      <span className="text-sm">
        {value || <span className="text-muted-foreground italic">—</span>}
      </span>
    );
  }

  if (type === "password") {
    return (
      <div className="flex items-center gap-1">
        <Input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          className={`${baseClassName} ${className || ""}`}
          data-testid={testId}
          aria-label={ariaLabel}
        />
        <Button aria-label={showPassword ? "Hide password" : "Show password"}
          type="button"
          size="icon"
          variant="ghost"
          className="h-6 w-6 shrink-0"
          onClick={() => setShowPassword(!showPassword)}
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          {showPassword ? (
            <EyeOff className="h-3 w-3" />
          ) : (
            <Eye className="h-3 w-3" />
          )}
        </Button>
      </div>
    );
  }

  if (type === "tel") {
    return (
      <Input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="tel"
        inputMode="tel"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder || "(555) 000-0000"}
        autoComplete="new-password"
        className={`${baseClassName} ${className || ""}`}
        data-testid={testId}
        aria-label={ariaLabel}
      />
    );
  }

  if (type === "number") {
    return (
      <Input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="number"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder || "0"}
        className={`${baseClassName} ${className || ""}`}
        data-testid={testId}
        aria-label={ariaLabel}
      />
    );
  }

  if (type === "email") {
    // s236 (EMAIL-FORMAT-1): the quiet line under the box; the value saves as typed.
    return (
      <div className="w-full">
      <Input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="email"
        inputMode="email"
        autoCapitalize="none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder || "name@example.com"}
        autoComplete="new-password"
        className={`${baseClassName} ${className || ""}`}
        data-testid={testId}
        aria-label={ariaLabel}
      />
      <EmailHint value={value} testId={testId ? `${testId}-email-hint` : undefined} />
      </div>
    );
  }

  if (type === "currency") {
    // Show formatted display when not focused; show raw number string when focused
    const displayVal = isCurrencyFocused
      ? value.replace(/[^0-9.-]/g, "")
      : value ? formatPlannerCurrency(value) : "";
    return (
      <Input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="text"
        inputMode="decimal"
        value={displayVal}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsCurrencyFocused(true)}
        onBlur={() => { setIsCurrencyFocused(false); onBlur(); }}
        placeholder={placeholder || "0.00"}
        autoComplete="new-password"
        className={`${baseClassName} ${className || ""}`}
        data-testid={testId}
        aria-label={ariaLabel}
      />
    );
  }

  if (type === "date") {
    if (readOnly) {
      const display = formatDate(value);
      return (
        <span className="text-sm">
          {display || <span className="text-muted-foreground italic">—</span>}
        </span>
      );
    }
    return (
      <Input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="date"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={`${baseClassName} ${className || ""}`}
        data-testid={testId}
        aria-label={ariaLabel}
      />
    );
  }

  // yes_no — a compact Yes/No select. The stored value is the "true"/"false" string
  // (same convention as the card-path Switch), so showIf conditions keyed on "true"
  // evaluate correctly (s104, DB-37 follow-on / Item 5).
  if (type === "yes_no") {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={`h-8 w-full text-sm border border-border bg-muted hover:bg-muted-foreground/20 px-2 rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:bg-background ${className || ""}`}
        data-testid={testId}
        aria-label={ariaLabel}
      >
        <option value="">Select...</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }

  if (multiline) {
    return (
      <Textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete="new-password"
        // In a table cell (compact) start at one row so the textarea falls to the
        // min-h-[32px] floor and aligns with single-line Input cells; overflow
        // scrolls internally. The drawer/card path (no compact) keeps the default
        // multi-row height for spacious editing.
        rows={compact ? 1 : undefined}
        className={`${multilineClassName} ${className || ""}`}
        data-testid={testId}
        aria-label={ariaLabel}
      />
    );
  }

  if (options && options.length > 0) {
    // FIELD-AUDIT-1 Phase 3 item 5 (0c, D-153.3/D-154.5, ruling A): 2–4 options in a DRAWER /
    // form context (!compact) → radios; ribbon + compact table cells keep the dropdown (radios
    // don't fit a one-line cell). readOnly is handled by the early return above, so this path is
    // always editable. Value strings preserved exactly; unanswered ("") highlights no option.
    if (!compact && options.length >= 2 && options.length <= 4) {
      const radioName = `radio-${field?.id ?? ariaLabel ?? "cell"}-${rowId ?? "0"}`;
      return (
        <RadioOptions
          name={radioName}
          value={value}
          options={options.map((c) => ({ value: c, label: c }))}
          onChange={(v) => { onChange(v); onBlur(); }}
          testIdPrefix={testId}
          className={className}
        />
      );
    }
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={`h-8 w-full text-sm border border-border bg-muted hover:bg-muted-foreground/20 px-2 rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:bg-background ${className || ""}`}
        data-testid={testId}
        aria-label={ariaLabel}
      >
        <option value="">Select...</option>
        {options.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
    );
  }

  return (
    <Input
      ref={inputRef as React.RefObject<HTMLInputElement>}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      autoComplete="new-password"
      className={`${baseClassName} ${className || ""}`}
      data-testid={testId}
      aria-label={ariaLabel}
    />
  );
}
